import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../lib/animations';
import AssessmentLayout from '../components/assessment/AssessmentLayout';
import AssessmentQuestionCard from '../components/assessment/AssessmentQuestionCard';
import ChildProgressBar from '../components/assessment/ChildProgressBar';
import StepIndicator from '../components/assessment/StepIndicator';
import {
  useAssessmentQuestions,
  useStartAssessment,
  useSaveResponse,
  useSubmitAssessment,
} from '../hooks/useAssessment';
import type { AssessmentResponseValue, Assessment, ChildAvatarKey } from '../types/assessment.types';
import {
  TOTAL_STEPS,
  FLOW_PARAMS,
  FLOW_TYPES,
  getNextCategory,
  completeOnboarding,
  isOnboardingActive,
} from '../constants/onboardingFlow';

const categoryDescriptions: Record<string, { line1: string; line2?: string }> = {
  language_and_communication: {
    line1: 'Language is how your little one connects with you and the world - making friends, asking for what they need, sharing what they feel.',
    line2: 'A few questions here help us spark the right moments for them.',
  },
  personal_social_and_emotional: {
    line1: 'This is how your little one feels safe, builds confidence, and connects with others - managing big feelings, making friends, learning who they are. A few questions here help us spark the right moments for them.',
  },
};

export default function AssessmentPage() {
  const { track } = useAnalytics();
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();


  const numericChildId = parseInt(childId || '0', 10);

  const [searchParams] = useSearchParams();
  const categoryKey = searchParams.get(FLOW_PARAMS.CATEGORY) ?? 'language_and_communication';
  const flowType = searchParams.get(FLOW_PARAMS.FLOW_TYPE);
  const stepParam = searchParams.get(FLOW_PARAMS.STEP_NUMBER);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryKey]);

  // Check if we're in onboarding flow
  const isOnboarding = flowType === FLOW_TYPES.ONBOARDING && isOnboardingActive();
  const currentStep = stepParam ? parseInt(stepParam, 10) : null;

  // Assessment state
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [responses, setResponses] = useState<Record<number, AssessmentResponseValue>>({});
  const [childInfo, setChildInfo] = useState<{
    name: string;
    fromAge: number;
    toAge: number;
    avatarKey: string;
  } | null>(null);

  const startAssessment = useStartAssessment();
  const submitAssessment = useSubmitAssessment();

  // Fetch questions once we know the age range
  const {
    data: questions,
    isLoading: questionsLoading,
  } = useAssessmentQuestions(
    categoryKey,
    childInfo?.fromAge ?? 0,
    childInfo?.toAge ?? 0,
    childInfo !== null && assessment !== null
  );

  // Save response mutation — uses assessmentId
  const saveResponse = useSaveResponse(assessment?.id ?? 0);

  // Start or resume assessment on mount
  useEffect(() => {
    if (!numericChildId) return;

    async function init() {
      try {
        const { api } = await import('../services/api');

        // Get child info
        const children = await api.getChildren();
        const child = children.find((c) => c.id === numericChildId);
        if (!child) {
          navigate('/journey/child/new', { replace: true });
          return;
        }

        setChildInfo({
          name: child.nickname,
          fromAge: child.fromAge,
          toAge: child.toAge,
          avatarKey: child.avatarKey,
        });

        // Start or resume assessment — backend resolves categoryKey internally
        const assessmentData = await startAssessment.mutateAsync({
          childId: numericChildId,
          categoryKey,
        });

        setAssessment(assessmentData);
        track('assessment_start', { child_id: String(numericChildId) });

        // Restore saved responses
        if (assessmentData.responses.length > 0) {
          const restored: Record<number, AssessmentResponseValue> = {};
          assessmentData.responses.forEach((r) => {
            restored[r.questionId] = r.response;
          });
          setResponses(restored);
        }
      } catch (error) {
        console.error('Failed to initialize assessment:', error);
      }
    }

    init();
  }, [numericChildId, categoryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectResponse = useCallback(
    async (questionId: number, response: AssessmentResponseValue) => {
      if (!assessment) return;

      track('assessment_answer', {
        child_id: String(numericChildId),
        question_id: String(questionId),
        answer_value: String(response),
      });

      // Optimistic update
      setResponses((prev) => ({ ...prev, [questionId]: response }));

      // Save to server
      try {
        await saveResponse.mutateAsync({ questionId, response });
      } catch (error) {
        // Revert on failure
        setResponses((prev) => {
          const reverted = { ...prev };
          delete reverted[questionId];
          return reverted;
        });
        console.error('Failed to save response:', error);
      }
    },
    [assessment, saveResponse]
  );

  const answeredCount = Object.keys(responses).length;
  const totalCount = questions?.length ?? 0;
  const allAnswered = totalCount > 0 && answeredCount >= totalCount;

  async function handleSubmit() {
    if (!assessment || !allAnswered || submitAssessment.isPending || !childInfo) return;

    track('assessment_completed', { child_id: String(numericChildId), total_questions: String(totalCount) });

    try {
      const hasAgeRange = childInfo.fromAge != null && childInfo.toAge != null;

      const result = await submitAssessment.mutateAsync({
        assessmentId: assessment.id,
        retake: !isOnboarding,
        localHour: new Date().getHours(),
        ageRanges: hasAgeRange ? [{ fromAge: childInfo.fromAge, toAge: childInfo.toAge }] : undefined,
      });

      // Sequential navigation logic
      if (isOnboarding) {
        const nextCategory = getNextCategory(categoryKey);

        if (nextCategory) {
          // Navigate to next assessment in sequence
          navigate(
            `/assessment/${result.childId}?${FLOW_PARAMS.CATEGORY}=${nextCategory.key}&${FLOW_PARAMS.FLOW_TYPE}=${FLOW_TYPES.ONBOARDING}&${FLOW_PARAMS.STEP_NUMBER}=${nextCategory.step}`,
            { replace: true }
          );
        } else {
          // Final step completed — clear onboarding flag, show completion screen
          completeOnboarding();
          navigate(`/assessment/${result.childId}/completed`, { replace: true });
        }
      } else {
        // Standalone retake — show completion screen
        navigate(`/assessment/${result.childId}/completed`, { replace: true });
      }
    } catch (error) {
      console.error('Failed to submit assessment:', error);
    }
  }

  const isLoading = questionsLoading || startAssessment.isPending;

   const desc = useMemo(() => categoryDescriptions[categoryKey], [categoryKey])

  // Header
  const header = (
    <div className="px-6 pt-5 pb-4 bg-[#DBF7F8]">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-black text-[#1B2831] uppercase tracking-[0.72px]">
            QUICK CHECK-IN
          </p>

          {/* Conditionally render step indicator */}
          {isOnboarding && currentStep && (
            <div className="mt-2">
              <StepIndicator
                currentStep={currentStep}
                totalSteps={TOTAL_STEPS}
                className="text-left"
              />
            </div>
          )}

          <h1 className="text-2xl font-semibold text-[#1B2831] tracking-[-0.96px] mt-1">
            {assessment?.categoryName || 'Language & communication'}
          </h1>
        </div>
        <button
          type="button"
          onClick={() => {
            track('assessment_exit', {
              child_id: String(numericChildId),
              exit_question_number: String(answeredCount),
              total_questions: String(totalCount),
            });
            navigate(-1);
          }}
          className="w-6 h-6 flex items-center justify-center mt-1"
          aria-label="Close assessment"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1L13 13M13 1L1 13" stroke="#1B2831" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );

  // Footer
  const footer = childInfo ? (
    <ChildProgressBar
      avatarKey={childInfo.avatarKey as ChildAvatarKey}
      childName={childInfo.name}
      fromAge={childInfo.fromAge}
      toAge={childInfo.toAge}
      answeredCount={answeredCount}
      totalCount={totalCount}
    />
  ) : null;

  return (
    <AssessmentLayout
      header={header}
      footer={footer}
      backgroundColor="#DBF7F8"
    >
      {isLoading ? (
        /* Loading skeleton */
        <div className="py-8 space-y-8">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse space-y-3">
              <div className="h-5 bg-[#C7EDEF] rounded w-full" />
              <div className="h-5 bg-[#C7EDEF] rounded w-3/4" />
              <div className="space-y-2 mt-3">
                <div className="h-12 bg-white rounded-lg border-2 border-[#C7EDEF]" />
                <div className="h-12 bg-white rounded-lg border-2 border-[#C7EDEF]" />
                <div className="h-12 bg-white rounded-lg border-2 border-[#C7EDEF]" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <motion.div
          className="py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Description text */}
          <motion.div variants={itemVariants} className="text-center mb-8">
            {(() => {
             
              if (!desc) return null;
              return (
                <>
                  <p className="text-sm text-[#323B46]">{desc.line1}</p>
                  {desc.line2 && <p className="text-sm text-[#323B46] mt-2">{desc.line2}</p>}
                </>
              );
            })()}
          </motion.div>

          {/* Questions list */}
          <div className="flex flex-col gap-12">
            {questions?.map((question) => (
              <motion.div key={question.id} variants={itemVariants}>
                <AssessmentQuestionCard
                  questionNumber={question.sortOrder}
                  text={question.text}
                  selectedResponse={responses[question.id] ?? null}
                  onSelect={(response) => handleSelectResponse(question.id, response)}
                  isSaving={saveResponse.isPending}
                />
              </motion.div>
            ))}
          </div>

          {/* Submit area */}
          <motion.div variants={itemVariants} className="mt-12 pb-4">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allAnswered || submitAssessment.isPending}
              className={`
                w-full h-12 rounded-xl font-black text-xs uppercase tracking-[0.72px]
                transition-all
                ${allAnswered
                  ? 'bg-[#1B2831] text-white'
                  : 'bg-[#A9B7BE] text-[#4D5762] cursor-not-allowed'
                }
              `}
            >
              {submitAssessment.isPending ? 'Submitting...' : 'Submit Answers'}
            </button>
            {!allAnswered && (
              <p className="text-xs text-[#4D5762] text-center mt-2 font-medium">
                You need to reply to all questions to be able to submit your answer
              </p>
            )}
            {submitAssessment.isError && (
              <p className="text-xs text-red-600 text-center mt-2">
                {submitAssessment.error.message}
              </p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AssessmentLayout>
  );
}
